import React, { useState } from 'react'
import { AppBar, Toolbar, Typography, IconButton, Box, Badge } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import NotificationsIcon from '@mui/icons-material/Notifications'
import AccountCircle from '@mui/icons-material/AccountCircle'
import MoreIcon from '@mui/icons-material/MoreVert'
import MailIcon from '@mui/icons-material/Mail'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import UserIcon from '../misc/Avatar'
import Link from '../navigation/Link'
import CustomMenu from '../menu/Menu'

const APP_BAR_HEIGHT = 64

const renderBadgeIcon = (icon, count, label, onClick, to) => (
  <IconButton
    {...(to && { component: Link, to })}
    size='large'
    aria-label={`show ${count} ${label}`}
    color='inherit'
    onClick={onClick}
  >
    <Badge badgeContent={count} color='error'>
      {icon}
    </Badge>
  </IconButton>
)

const TopAppBar = ({ open, handleDrawerToggle, handleOpenNotif, unReadNotifications, unReadEmail }) => {
  const { user, logOut } = useAuth()
  const location = useLocation()

  const [menuDesktopAnchor, setMenuDesktopAnchor] = useState(null)
  const [menuMobileAnchor, setMenuMobileAnchor] = useState(null)

  const isMenuDesktopOpen = Boolean(menuDesktopAnchor)
  const isMobileMenuOpen = Boolean(menuMobileAnchor)

  const menuDesktopId = 'desktop-menu'
  const mobileMenuId = 'menu-mobile'

  const handleMenuDesktopOpen = (event) => setMenuDesktopAnchor(event.currentTarget)
  const handleMenuDesktopClose = () => setMenuDesktopAnchor(null)

  const handleMenuMobileOpen = (event) => setMenuMobileAnchor(event.currentTarget)
  const handleMenuMobileClose = () => setMenuMobileAnchor(null)

  // Hide bar on login page or if no user
  if (!user || location.pathname === '/login') return null

  const userLogo = user.attachments?.find((a) => a.type === 'user_logo')
  const itemState = { history: location.pathname, id: location.state?.id ?? null }

  const menuCommonProps = {
    anchorOrigin: { vertical: 'top', horizontal: 'right' },
    transformOrigin: { vertical: 'top', horizontal: 'right' }
  }

  return (
    <AppBar sx={{ height: APP_BAR_HEIGHT }} position='fixed' open={open}>
      <Toolbar>
        <IconButton color='inherit' aria-label='open drawer' onClick={handleDrawerToggle} edge='start' sx={{ mr: 5 }}>
          <MenuIcon />
        </IconButton>

        <Typography variant='h6' noWrap component='div'>
          {process.env.REACT_APP_TITLE}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          {renderBadgeIcon(<MailIcon />, unReadEmail, 'new mails', null, '/emails')}
          {renderBadgeIcon(<NotificationsIcon />, unReadNotifications, 'new notifications', handleOpenNotif)}

          <IconButton
            size='large'
            edge='end'
            aria-label='account of current user'
            aria-controls={menuDesktopId}
            aria-haspopup='true'
            onClick={handleMenuDesktopOpen}
            color='inherit'
          >
            {userLogo ? <UserIcon size='small' img={userLogo} /> : <AccountCircle />}
          </IconButton>
        </Box>

        <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
          <IconButton
            size='large'
            aria-label='show more'
            aria-controls={mobileMenuId}
            aria-haspopup='true'
            onClick={handleMenuMobileOpen}
            color='inherit'
          >
            <MoreIcon />
          </IconButton>
        </Box>
      </Toolbar>

      <CustomMenu
        {...menuCommonProps}
        id={menuDesktopId}
        open={isMenuDesktopOpen}
        anchorEl={menuDesktopAnchor}
        onClose={handleMenuDesktopClose}
        items={[
          {
            rights: { module: 'MyProfile', is_view: true },
            onClick: handleMenuDesktopClose,
            state: itemState,
            component: Link,
            to: '/myprofile',
            label: 'Mon Profile'
          },
          {
            rights: { module: 'MySettings', is_view: true },
            onClick: handleMenuDesktopClose,
            state: itemState,
            component: Link,
            to: '/mysettings',
            label: 'Mes Paramètres'
          },
          {
            onClick: () => {
              logOut()
              handleMenuDesktopClose()
            },
            label: 'Déconnexion'
          }
        ]}
      />

      <CustomMenu
        {...menuCommonProps}
        id={mobileMenuId}
        open={isMobileMenuOpen}
        anchorEl={menuMobileAnchor}
        onClose={handleMenuMobileClose}
        items={[
          {
            rights: { module: 'Emails', is_access: true },
            icon: <MailIcon />,
            badgeContent: unReadEmail,
            onClick: handleMenuMobileClose,
            state: itemState,
            component: Link,
            to: '/emails',
            label: 'Email'
          },
          {
            rights: { module: 'MySettings', is_view: true },
            icon: <NotificationsIcon />,
            badgeContent: unReadNotifications,
            onClick: () => {
              handleOpenNotif()
              handleMenuMobileClose()
            },
            state: itemState,
            label: 'Notifications'
          },
          {
            icon: userLogo ? <UserIcon sx={{ mr: 1 }} size='small' img={userLogo} /> : <AccountCircle />,
            onClick: (e) => {
              handleMenuDesktopOpen(e)
              handleMenuMobileClose()
            },
            label: 'Mon Profile'
          }
        ]}
      />
    </AppBar>
  )
}

export default TopAppBar
